export function imageIdentity(src) {
  try {
    const url = new URL(src, location.href);
    url.searchParams.delete('width');
    return url.href;
  } catch {
    return src;
  }
}

export function responsiveImageURL(src) {
  try {
    const url = new URL(src, location.href);
    const originalWidth = Number(url.searchParams.get('width'));
    if (originalWidth > 0) {
      const width = window.innerWidth < 750 ? (window.devicePixelRatio > 1 ? 800 : 600) : 1000;
      url.searchParams.set('width', Math.min(originalWidth, width));
    }
    return originalWidth > 0 ? url.href : src;
  } catch {
    return src;
  }
}

export function adjacentVariantImages(variants, selected, limit = 2) {
  if (!selected) return [];
  const values = selected.options.map((_, index) => [...new Set(variants.map(variant => variant.options[index]))]);
  const seen = new Set([imageIdentity(selected.image)]);
  return variants.filter(variant => {
    if (!variant.available || !variant.image || variant.id === selected.id) return false;
    const changed = variant.options.map((value, index) => value === selected.options[index] ? -1 : index).filter(index => index !== -1);
    if (changed.length !== 1) return false;
    const index = changed[0];
    return Math.abs(values[index].indexOf(variant.options[index]) - values[index].indexOf(selected.options[index])) === 1;
  }).filter(variant => {
    const identity = imageIdentity(variant.image);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  }).slice(0, limit).map(variant => variant.image);
}

export class VariantImageCache {
  constructor() {
    this.entries = new Map();
    this.generation = 0;
  }
  load(source, priority = 'high') {
    const src = responsiveImageURL(source);
    const cached = this.entries.get(src);
    if (cached) {
      if (priority === 'high') cached.image.fetchPriority = 'high';
      this.entries.delete(src);
      this.entries.set(src, cached);
      return cached.promise;
    }
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = priority;
    const entry = {image, ready:false};
    image.src = src;
    entry.promise = image.decode().then(() => {
      entry.ready = true;
      this.trim();
      return src;
    }).catch(error => {
      this.entries.delete(src);
      throw error;
    });
    this.entries.set(src, entry);
    this.trim();
    return entry.promise;
  }
  trim() {
    for (const [key, entry] of this.entries) {
      if (this.entries.size <= 12) break;
      if (entry.ready) this.entries.delete(key);
    }
  }
  canPreload() {
    const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
    return !connection?.saveData && !/^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '') && !document.hidden;
  }
  warm(src) {
    if (!src || !this.canPreload()) return;
    this.trim();
    if (this.entries.size >= 12 && !this.entries.has(responsiveImageURL(src))) return;
    this.load(src, 'low').catch(() => {});
  }
  schedule(variants, selected, isCurrent) {
    this.cancel();
    if (!selected || !this.canPreload()) return;
    const generation = this.generation;
    this.timer = setTimeout(() => {
      if (generation !== this.generation || !isCurrent() || !this.canPreload()) return;
      adjacentVariantImages(variants, selected).forEach(src => this.warm(src));
    }, 200);
  }
  cancel() {
    clearTimeout(this.timer);
    this.generation++;
  }
}
